import type { DocumentItem } from './model.js';
import { getValueAtPath } from './path.js';

/** Where a badge reads its value: a dot path, or a function. */
export type BadgeSource = string | ((item: DocumentItem) => unknown);

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export interface BadgeOptions {
  /** Where the value lives. Defaults to the column's own key. */
  value?: BadgeSource;
  /**
   * Map a value to a tone, e.g. `{ approved: 'success', draft: 'neutral' }`.
   * A function gets the final say for anything a map cannot express.
   */
  tones?: Record<string, BadgeTone> | ((value: string, item: DocumentItem) => BadgeTone | undefined);
  /** Rewrite the visible text, e.g. `in_review` -> `In review`. */
  format?: (value: string, item: DocumentItem) => string;
  /** Render as a filled pill rather than plain coloured text. */
  variant?: 'text' | 'pill';
  /** Tone for values with no mapping. Default `'neutral'`. */
  fallbackTone?: BadgeTone;
}

export interface ResolvedBadge {
  value: string;
  label: string;
  tone: BadgeTone;
  variant: 'text' | 'pill';
}

/**
 * Tones a value would conventionally carry, so the common statuses look right
 * with no configuration at all. Anything here is overridden by `tones`.
 */
const CONVENTIONAL_TONES: Record<string, BadgeTone> = {
  approved: 'success',
  active: 'success',
  complete: 'success',
  completed: 'success',
  published: 'success',
  done: 'success',
  success: 'success',
  review: 'warning',
  'in review': 'warning',
  in_review: 'warning',
  pending: 'warning',
  warning: 'warning',
  rejected: 'danger',
  failed: 'danger',
  error: 'danger',
  expired: 'danger',
  archived: 'neutral',
  draft: 'neutral',
  new: 'info',
  info: 'info',
};

/** Title-case a raw value: `in_review` -> `In review`. */
export function humanizeBadge(value: string): string {
  const spaced = value.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Resolve everything a renderer needs to draw one badge.
 *
 * Framework-free, so React and Angular produce the same tone and label — and
 * so the defaults are decided in exactly one place.
 *
 * Returns `null` when there is no value, so rows without a status render an
 * empty cell rather than a meaningless grey pill.
 */
export function resolveBadge(
  item: DocumentItem,
  columnKey: string,
  options: BadgeOptions = {},
): ResolvedBadge | null {
  const source = options.value ?? columnKey;
  const raw = typeof source === 'function' ? source(item) : getValueAtPath(item, source);

  if (raw === null || raw === undefined || raw === '') return null;
  const value = typeof raw === 'string' ? raw : String(raw);

  let tone: BadgeTone | undefined;
  if (typeof options.tones === 'function') tone = options.tones(value, item);
  else if (options.tones) tone = options.tones[value];

  tone ??= CONVENTIONAL_TONES[value.toLowerCase()] ?? options.fallbackTone ?? 'neutral';

  return {
    value,
    label: options.format ? options.format(value, item) : humanizeBadge(value),
    tone,
    variant: options.variant ?? 'text',
  };
}
